<template>
    <div class="DropdownButton">
        <ToggleButton
            :open="open"
            :option="selectedOption"
            :disabled="disabled"
            :type="type"
            :icon-color="defaultIconColor"
            :hide-arrow="hideArrow"
            @click="toggleOpen"
        >
            <template v-slot:default>
                <!--
                    @slot Replace the text of the dropdown toggle button
                -->
                <slot></slot>
            </template>
        </ToggleButton>

        <div
            v-if="open && showDropdown"
            :class="[
                'DropdownButton__options-container',
                `DropdownButton__options-container--${dropdownPosition}`
            ]"
        >
            <!--
                @slot Replace the contents of the dropdown
            -->
            <slot name="dropdown">
                <div class="DropdownButton__dropdown">
                    <template v-for="opt in optionsFormatted">
                        <!--
                            @slot Replace the individual dropdown items
                                @binding {object} option An object containing properties `label` and `id`
                        -->
                        <slot
                            name="option"
                            :option="opt"
                        >
                            <Option
                                :key="opt.label"
                                :option="opt"
                                :is-active="opt.value === selectedOption.value"
                                :show-selection="showSelection"
                                :required="required"
                                @click="optionClick"
                            />
                        </slot>
                    </template>
                </div>
            </slot>
        </div>
    </div>
</template>
<script>
import Option from './_option.vue';
import ToggleButton from './_toggle-button.vue';
import colors from 'style/_colors.scss';
import isEqual from 'lodash/isEqual';

export default {
    name: 'RPDropdownButton',
    components: {
        Option,
        ToggleButton
    },
    props: {
        /**
         * @values primary, secondary, tertiary
         */
        type: {
            type: String,
            default: 'primary',
            validator: val => ['primary', 'secondary', 'tertiary'].indexOf(val) !== -1,
        },
        /**
         * @model
        */
        value: {
            type: [String, Boolean, Number, Object],
            default: -9999
        },
        /**
         * An array of numbers, strings, or objects with `value` and `label` properties:
         * {value: opt, label: opt}
         */
        options: {
            type: Array,
            default: () => ([]),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * When true, a value must be selected. When false, the selected
         * option can be clicked to unselect it.
         */
        required: {
            type: Boolean,
            default: false,
        },
        /**
         * A label to show when no option is selected.
         */
        defaultLabel: {
            type: String,
            default: 'Select',
        },
        /**
         * An icon to show when no option is selected.
         * Instructions for importing individual Streamline components can be found in the Streamline app.
         */
        defaultIcon: {
            type: [Object, Array],
            default: () => ({}),
        },
        /**
         * TODO: implement
         */
        defaultIconSize: {
            type: Number,
            default: 16,
        },
        /**
         * Any CSS color
         */
        defaultIconColor: {
            type: String,
            default: colors.grey600,
        },
        dropdownPosition: {
            type: String,
            default: 'right',
            validator: val => ['left', 'right', 'center'].indexOf(val) !== -1
        },
        /**
         * Show the currently selected option instead of the `defaultLabel`.
         */
        showSelection: {
            type: Boolean,
            default: true,
        },
        showDropdown: {
            type: Boolean,
            default: true,
        },
        hideArrow: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            open: false,
        };
    },
    computed: {
        optionsFormatted(){
            return this.options.map(opt => {
                return (typeof opt === 'string'
                        || typeof opt === 'number')
                    ? {
                        value: opt,
                        label: opt,
                    }
                    : opt;
            });
        },
        selectedOption(){
            const temp = this.optionsFormatted.find(opt => isEqual(opt.value, this.value));
            return (this.showSelection && !!temp)
                ? temp
                : {
                    label: this.defaultLabel,
                    icon: this.defaultIcon,
                    iconSize: this.defaultIconSize,
                };
        },
    },

    watch: {
        open(newVal, oldVal){
            if(newVal !== oldVal){
                this.$emit('button-state', newVal);
            }
        }
    },

    mounted(){
        document.addEventListener('click', this.close);
    },
    destroyed() {
        document.removeEventListener('click', this.close);
    },
    methods: {
        close() {
            this.open = false;
        },
        toggleOpen() {
            this.open = !this.open;
            this.$emit('button-state', this.open);
        },
        optionClick(val){
            /**
            * This event is fired to support `v-model`, and shouldn't be used directly.
            */
            this.$emit('input', val);
            this.toggleOpen();
        }
    }
};
</script>
<style lang="scss">
    .DropdownButton {
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
        position: relative;
        display: inline-block;

        &__options-container {
            position:absolute;
            z-index: 1000;
            border: 1px solid $grey-100;
            box-sizing: border-box;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
            border-radius: 4px;
            margin-top: 0.25rem;
            display: flex;
            flex-direction: column;
            background: $white;
            &--right {
                right: 0;
            }
            &--left {
                left: 0;
            }
            &--center {
                left: 50%;
                transform: translateX(-50%);
            }
        }
        &__dropdown {
            padding: 0 0.5rem 0.5rem 0.5rem;
        }
    }
</style>

<docs>
Basic example:

```vue
<RPDropdownButton
    :options="['Apple', 'Banana', 'Orange']"
    v-model="myVal"
></RPDropdownButton>
```


```vue
import { House1 } from "@reviewpush/streamline-regular/lib/interface-essential/Home";
<RPDropdownButton
    :options="['Apple', 'Banana', 'Orange']"
    :defaultIcon="House1"
    v-model="myVal"
></RPDropdownButton>
```

</docs>
